import dynamic from 'next/dynamic'
import Link from 'next/link'
import { useTranslation } from 'next-i18next'
import styled from 'styled-components'

import { breakpoints, colors } from '@/styles'
import { useCitiesInState, useStateData } from '@/lib/locations'
// import { ProvinceDropdown } from '@/features/States'
import Breadcrumbs from '@/components/breadcrumbs'
import Head from '@/components/head'
import { H3, H5, Label, ParagraphRegular } from '@/components/text'

const ProvinceDropdown = dynamic(
  () =>
    import('@/features/States').then(components => components.ProvinceDropdown),
  { ssr: false }
)

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 24px 20px 80px;
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 1240px;
`

const TitleWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 230px;
  gap: 80px;

  @media (max-width: ${breakpoints.ipadMiniMax}) {
    grid-template-columns: 1fr;
  }
`

const TitleContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: ${colors.grey24};

  h1 {
    color: ${colors.black};
    margin: 0;
  }
`

const DropdownWrapper = styled.div`
  display: block;
  @media (max-width: ${breakpoints.ipadMiniMax}) {
    display: none;
  }
`
const MobileDropdownWrapper = styled.div`
  display: none;
  @media (max-width: ${breakpoints.ipadMiniMax}) {
    margin-top: 20px;
    display: block;
  }
`

const CitiesWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;

  h2 {
    color: ${colors.black};
    margin: 0;
  }
`

const CitiesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px 32px;

  a {
    color: ${colors.blue40};
    text-decoration: none;
  }
`

const StatePage = ({ alphaCode }) => {
  const { t } = useTranslation()
  const { name, shortName, description, slug, country } =
    useStateData(alphaCode)
  const { data: cities } = useCitiesInState(alphaCode)
  const countryName = country === 'US' ? 'United States' : 'Canada'

  const metaTitle = t('states:Province Meta Title', {
    defaultValue: 'Apartments for Rent in {{ provinceName }} | liv.rent',
    provinceName: name
  })
  const metaDescription = t('states:Province Meta Description', {
    defaultValue:
      'Search apartments, townhouses, and rooms for rent in {{ provinceName }}, {{ countryName }}',
    provinceName: name,
    countryName
  })

  const breadcrumbs = [
    { label: t('states:Home'), path: '/' },
    {
      label: shortName,
      path: `/rental-listings/${slug}`
    }
  ]

  return (
    <Wrapper>
      <Head
        title={metaTitle}
        description={metaDescription}
        path={`/rental-listings/${slug}`}
      />
      <Content>
        <Breadcrumbs items={breadcrumbs} />
        <TitleWrapper>
          <TitleContent>
            <H3 as="h1">{name}</H3>
            {description && <ParagraphRegular>{description}</ParagraphRegular>}
          </TitleContent>
          <DropdownWrapper>
            <ProvinceDropdown value={slug} />
          </DropdownWrapper>
        </TitleWrapper>
        <CitiesWrapper>
          <H5 as="h2">
            {t('states:all_cities_in_province', {
              defaultValue: 'All Cities in {{ provinceName }}',
              provinceName: name
            })}
          </H5>
          <CitiesGrid>
            {cities.map(city => (
              <Link key={city.slug} href={`/rental-listings/city/${city.slug}`}>
                <Label fontSize="14px" type="small">
                  {t(`cities:city_name_${city.slug}`, city.name)}
                </Label>
              </Link>
            ))}
          </CitiesGrid>
        </CitiesWrapper>
        <MobileDropdownWrapper>
          <ProvinceDropdown value={slug} />
        </MobileDropdownWrapper>
      </Content>
    </Wrapper>
  )
}

export default StatePage
